import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Phone, CalendarCheck, Clock } from "lucide-react";
import { useLang } from "../LanguageContext";
import { CONTACT } from "../content";
import { Reveal } from "./Reveal";

const wa = (msg) => `https://wa.me/${CONTACT.reserveWa}?text=${encodeURIComponent(msg)}`;

export const Reserve = () => {
  const { t } = useLang();
  const r = t.reserve2;
  const f = r.form;
  const [showForm, setShowForm] = useState(false);
  const [data, setData] = useState({ name: "", phone: "", email: "", date: "", time: "", guests: "", meal: "", special: "" });

  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const L = f.labels;
    const lines = [
      r.form.intro,
      `${L.name}: ${data.name}`,
      `${L.phone}: ${data.phone}`,
      data.email ? `${L.email}: ${data.email}` : null,
      `${L.date}: ${data.date}`,
      `${L.time}: ${data.time}`,
      `${L.guests}: ${data.guests}`,
      `${L.meal}: ${data.meal}`,
      data.special ? `${L.special}: ${data.special}` : null,
      r.form.outro,
    ].filter(Boolean);
    window.open(wa(lines.join("\n")), "_blank", "noopener,noreferrer");
  };

  const options = [
    { key: "whatsapp", icon: MessageCircle, title: r.whatsapp, desc: r.whatsappDesc, href: wa(r.waTemplate), testid: "reserve-whatsapp" },
    { key: "call", icon: Phone, title: r.call, desc: r.callDesc, href: CONTACT.reservePhoneHref, testid: "reserve-call" },
    { key: "request", icon: CalendarCheck, title: r.request, desc: r.requestDesc, testid: "reserve-request" },
  ];

  const inputCls = "w-full bg-white/70 border border-sand rounded-lg px-4 py-3 text-sm text-brown placeholder:text-brown/40 outline-none focus:border-olive transition-colors";

  return (
    <section id="reserve" className="bg-sand/40 py-24 md:py-36 scroll-mt-24" data-testid="reserve-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-14 md:mb-16">
          <Reveal>
            <p className="text-olive text-xs uppercase tracking-[0.35em] mb-6">{r.chapter}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-brown text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">{r.title}</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-brown/60 text-lg font-light leading-relaxed">{r.lead}</p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {options.map((o, i) => {
            const Icon = o.icon;
            const inner = (
              <>
                <Icon className="text-gold" size={26} strokeWidth={1.5} />
                <h3 className="mt-5 font-serif text-brown text-2xl">{o.title}</h3>
                <p className="mt-2 text-brown/55 text-sm font-light">{o.desc}</p>
              </>
            );
            const cls = "block h-full text-left bg-ivory rounded-xl p-8 border border-sand shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-brown/10";
            return (
              <Reveal key={o.key} delay={i * 0.1} y={40}>
                {o.href ? (
                  <a href={o.href} target={o.key === "whatsapp" ? "_blank" : undefined} rel="noopener noreferrer" className={cls} data-testid={o.testid}>{inner}</a>
                ) : (
                  <button onClick={() => setShowForm((s) => !s)} className={`${cls} w-full`} data-testid={o.testid} aria-expanded={showForm}>{inner}</button>
                )}
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15} y={30}>
          <div className="mt-6 flex items-start gap-4 bg-ivory rounded-xl p-6 md:p-7 border border-sand shadow-sm" data-testid="reservation-hours">
            <Clock className="text-gold shrink-0 mt-0.5" size={22} strokeWidth={1.5} />
            <div>
              <h3 className="font-serif text-brown text-xl md:text-2xl">{r.hoursTitle}</h3>
              <p className="mt-2 text-brown/60 text-sm md:text-base font-light leading-relaxed max-w-2xl">{r.hoursText}</p>
            </div>
          </div>
        </Reveal>

        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
            >
              <form onSubmit={submit} className="mt-8 bg-ivory rounded-xl p-6 md:p-10 border border-sand" data-testid="reserve-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label={f.name}><input required value={data.name} onChange={set("name")} className={inputCls} data-testid="rf-name" /></Field>
                  <Field label={f.phone}><input required value={data.phone} onChange={set("phone")} className={inputCls} data-testid="rf-phone" /></Field>
                  <Field label={f.email}><input type="email" value={data.email} onChange={set("email")} className={inputCls} data-testid="rf-email" /></Field>
                  <Field label={f.date}><input required type="date" value={data.date} onChange={set("date")} className={inputCls} data-testid="rf-date" /></Field>
                  <Field label={f.time}><input required type="time" value={data.time} onChange={set("time")} className={inputCls} data-testid="rf-time" /></Field>
                  <Field label={f.guests}><input required type="number" min="1" value={data.guests} onChange={set("guests")} className={inputCls} data-testid="rf-guests" /></Field>
                  <Field label={f.meal}>
                    <select required value={data.meal} onChange={set("meal")} className={inputCls} data-testid="rf-meal">
                      <option value="" disabled>{f.mealPlaceholder}</option>
                      <option value={f.lunch}>{f.lunch}</option>
                      <option value={f.dinner}>{f.dinner}</option>
                    </select>
                  </Field>
                  <Field label={f.special} className="md:col-span-2"><textarea rows={2} value={data.special} onChange={set("special")} className={inputCls} data-testid="rf-special" /></Field>
                </div>
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
                  <button type="submit" data-testid="reserve-submit" className="inline-flex items-center justify-center gap-2 bg-olive text-ivory rounded-full px-8 h-14 text-sm tracking-wide transition-[background-color,transform] duration-300 hover:bg-brown hover:-translate-y-0.5">
                    <MessageCircle size={18} strokeWidth={1.8} />
                    {f.submit}
                  </button>
                  <p className="text-brown/50 text-xs font-light max-w-sm">{f.note}</p>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-olive text-xs uppercase tracking-[0.2em] mb-2">{label}</span>
    {children}
  </label>
);
